import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/dates'
import { useTicketEmails } from '@/queries/emailLog'
import { counterparty, emailStatusLabels, emailStatusVariants } from './emailStatus'
import { EmailDetailDialog } from './EmailDetailDialog'

// Body of the staff rail's "Emails" disclosure. Fetches only once the
// disclosure has been opened: most visits to a ticket never look.
export function TicketEmails({ ticketId, enabled }: { ticketId: string; enabled: boolean }) {
  const { data, isLoading, error } = useTicketEmails(ticketId, enabled)
  const [openId, setOpenId] = useState<string | undefined>()

  if (isLoading) return <p className="text-etn-micro text-etn-text-soft">Loading…</p>
  if (error) return <p className="text-etn-micro text-etn-danger-text">Couldn't load emails.</p>
  const rows = data?.data ?? []
  if (!rows.length) return <p className="text-etn-micro text-etn-text-soft">No emails for this ticket yet.</p>

  return (
    <div className="space-y-2">
      <ul className="space-y-1.5">
        {rows.map(entry => {
          const Icon = entry.direction === 'outbound' ? ArrowUpRight : ArrowDownLeft
          return (
            <li key={entry._id}>
              <button
                type="button"
                onClick={() => setOpenId(entry._id)}
                className="flex w-full items-center gap-2 rounded-etn-menu px-1 py-1 text-left text-etn-micro hover:bg-etn-surface"
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-etn-text-soft" aria-label={entry.direction === 'outbound' ? 'Outbound' : 'Inbound'} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-etn-text-cell">{counterparty(entry)}</span>
                  <span className="block text-etn-text-soft">{formatDateTime(entry.createdAt)}</span>
                </span>
                <Badge variant={emailStatusVariants[entry.status]}>{emailStatusLabels[entry.status]}</Badge>
              </button>
            </li>
          )
        })}
      </ul>
      <Link
        to={'/admin/email-log' as never}
        search={{ ticketId } as never}
        className="inline-block text-etn-micro font-semibold text-etn-accent-text-active hover:underline"
      >
        Open in email log
      </Link>
      <EmailDetailDialog id={openId} onClose={() => setOpenId(undefined)} />
    </div>
  )
}
